import type { LabAccount } from '@team-milva/lab-models/labAccount'
import type { TLabManagementPermission } from '@team-milva/lab-models/user'
import { appAuthRoutes } from '@/router/appAuthRoutes'
import { publicRoutes } from '@/router/publicRoutes'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { isLabAccountBlockingUser } from '@/globals/typescript/utils/labAccountAccess'
import { useAuthStore } from '@/stores/authStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useTurnaroundTimeStore } from '@/stores/turnaroundTimeStore'
import { useInviteStore } from '@/stores/inviteStore'
import { useLabEquipmentStore } from '@/stores/labEquipmentStore'
import { useLabLocationStore } from '@/stores/labLocationStore'
import { useBilagLibraryItemStore } from '@/stores/bilagLibraryItemStore'
import { useUserStore } from '@/stores/userStore'
import { useTextListStore } from '@/stores/textListStore'
import { usePricingSegmentStore } from '@/stores/pricingSegmentStore'
import { useAddonServiceStore } from '@/stores/addonServiceStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import { useOrderStore } from '@/stores/orderStore'
import { useInvoiceStore } from '@/stores/invoiceStore'
import { useClosedDayStore } from '@/stores/closedDayStore'
import { useDropPointStore } from '@/stores/dropPointStore'
import { useDhlShipmentStore } from '@/stores/dhlShipmentStore'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useDisplayStore } from '@/stores/displayStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useBcSettingsStore } from '@/stores/bcSettingsStore'
import { useBusinessCentralStore } from '@/stores/businessCentralStore'
import { getEnvTitlePrefix } from '@/globals/typescript/utils/envIndicator'
import { createRouter, createWebHistory } from 'vue-router'
import {
  confirmLeaveUnsaved,
  consumeLeaveConfirmation,
  getBlockingClaims,
  markLeaveConfirmed,
} from '@/composables/useUnsavedChangesGuard'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Public
    ...publicRoutes,

    // Authenticated
    {
      path: '/:labSlug',
      name: 'App',
      component: () => import('@/views/AppLayoutView.vue'),
      meta: {
        requiresAuth: true,
      },
      redirect: { name: 'Dashboard' },
      children: [...appAuthRoutes],
    },
    {
      path: '/:labSlug/update-password',
      name: 'UpdatePassword',
      meta: {
        requiresAuth: true,
      },
      component: () => import('@/views/UpdatePasswordView.vue'),
    },

    // Catch all
    {
      path: '/:labSlug/:pathMatch(.*)*',
      name: 'NotFound',
      meta: {
        requiresAuth: false,
      },
      redirect: { name: 'LabHome' },
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      meta: {
        requiresAuth: false,
      },
      redirect: { name: 'Home' },
    },
  ],
})

// Auth guard
router.beforeEach((to, from, next) => {
  const appStore = useAppStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()

  const waitAuth = setInterval(() => {
    if (!authStore.ready) {
      return
    }

    // Signed in, but the account has no user profile in either collection — it will never load,
    // so waiting on it below would hang on a blank page (MIL-483).
    if (authStore.isLoggedIn && userStore.isCurrentUserMissing) {
      authStore.logout({
        reason: 'no-profile',
        urlSlug: to.params.labSlug as string,
      })
      clearInterval(waitAuth)
      return
    }

    if (authStore.isLoggedIn && !userStore.currentUser) {
      return
    }

    if (
      authStore.isLoggedIn &&
      userStore.currentUser?.activeState !== 'active'
    ) {
      authStore.logout({
        reason: 'deactivated',
        urlSlug: to.params.labSlug as string,
      })
      clearInterval(waitAuth)
      return
    }

    clearInterval(waitAuth)

    if (authStore.isLoggedIn) {
      if (
        to.name !== 'UpdatePassword' &&
        userStore.currentUser?.options?.mustUpdatePassword
      ) {
        return next({
          name: 'UpdatePassword',
          params: { labSlug: to.params.labSlug },
        })
      }

      if (
        to.name === 'UpdatePassword' &&
        !userStore.currentUser?.options?.mustUpdatePassword
      ) {
        return next({
          name: 'App',
          params: { labSlug: to.params.labSlug },
        })
      }
    }

    const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
    if (requiresAuth && !authStore.user) {
      if (to.params.labSlug) {
        return next({ name: 'LabHome', params: { labSlug: to.params.labSlug } })
      } else {
        return next({ name: 'Home' })
      }
    } else if (
      !requiresAuth &&
      authStore.user &&
      to.name !== 'DesignSystem' &&
      to.name !== 'BcAdminConsentComplete' &&
      // A Display kiosk browser may also carry a stale/shared staff login
      // (e.g. someone testing the link, or a shared browser profile) — the
      // public Display screen must render regardless, not bounce to App.
      to.name !== 'PublicDisplay'
    ) {
      next({
        name: 'App',
        params: { labSlug: to.params.labSlug ?? appStore.defaultLabSlug },
      })
    } else {
      const requiresDeveloper = to.matched.some(
        (record) => record.meta.requiresDeveloper
      )
      if (requiresDeveloper && !userStore.isDeveloper) {
        return next({
          name: 'Dashboard',
          params: { labSlug: to.params.labSlug },
        })
      }
      next()
    }
  }, 50)
})

// Settings default-child guard
// This used to be a route-level `redirect` function on the `Settings` route
// (appAuthRoutes.ts). Vue Router resolves a matched route's `redirect`
// synchronously while building the very first navigation — inside
// `router.install()`, before any `beforeEach` guard runs and before
// `userStore.currentUser` has loaded on a cold/hard page load. That meant
// `canUser()` always saw no permissions yet and fell through to the `App`
// fallback (which itself redirects to `Dashboard`), so hard-loading
// `/:labSlug/settings` landed on the dashboard instead of the first
// accessible settings page. Doing the same check here instead is safe
// because this guard only runs after the Auth guard above has resolved,
// which guarantees `userStore.currentUser` is already loaded.
router.beforeEach((to, from, next) => {
  if (to.name !== 'Settings') {
    return next()
  }

  const userStore = useUserStore()
  // Order mirrors the "Lab" section in SettingsView.vue's menuSections.
  const firstAccessible: { name: string; permission: TLabManagementPermission }[] = [
    { name: 'SettingsSystemGeneral', permission: 'settingsGeneral.view' },
    { name: 'SettingsSystemBranding', permission: 'settingsBranding.view' },
    { name: 'LabLocations', permission: 'setup.view' },
    { name: 'Employees', permission: 'employees.view' },
    { name: 'LabEquipment', permission: 'equipment.view' },
  ]
  const entry = firstAccessible.find((item) => userStore.canUser(item.permission))

  next({ name: entry?.name ?? 'App', params: to.params })
})

// Account and slug guard
router.beforeEach(async (to, from, next) => {
  // Standalone public route with no labSlug param at all (the Entra admin-consent redirect
  // target) — every branch below either requires a labSlug or loads a lab account for one, both
  // meaningless here. Without this, a logged-in user always fails the labSlug-match check further
  // down (to.params.labSlug is always undefined for this route) and gets redirected away before
  // ever seeing the page.
  // Same reasoning applies to the public Display route — it also has no
  // labSlug param, and must render for a logged-in staff browser too (e.g.
  // someone testing the link, or a shared/kiosk browser with a stale login).
  if (to.name === 'BcAdminConsentComplete' || to.name === 'PublicDisplay') {
    return next()
  }

  const accountStore = useAccountStore()
  const authStore = useAuthStore()
  const userStore = useUserStore()
  const appStore = useAppStore()

  // Not logged in
  if (!authStore.isLoggedIn) {
    const labSlug = to.params.labSlug as string | undefined

    if (!labSlug) {
      return next()
    }

    let partialLabAccount: LabAccount | null = null
    try {
      partialLabAccount = await accountStore.getPartialLabAccount(
        to.params.labSlug as string
      )
    } catch {
      // Any Firestore error (most commonly permission-denied for unauthenticated reads)
      // — proceed without branding rather than breaking navigation.
      return next()
    }

    if (!partialLabAccount) {
      return next({ name: 'Home' })
    }

    return next()
  }

  // Logged in
  // - When logged in as a lab management user, load the account from the users accountID
  if (!userStore.isLabAdminUser) {
    const labAccount = await accountStore.getLabAccountFromUser()

    // A disabled lab blocks its own users, with the same message and sign-out as a disabled user
    // (MIL-419). Milva staff are exempt — they take the other branch below.
    if (
      isLabAccountBlockingUser({
        isLabAdminUser: userStore.isLabAdminUser,
        labAccount,
      })
    ) {
      await authStore.logout({
        reason: 'deactivated',
        urlSlug: to.params.labSlug as string,
      })
      return next(false)
    }

    if (to.name === 'DesignSystem') {
      return next()
    }

    if (labAccount?.subDomain !== to.params.labSlug) {
      return next({
        name: 'App',
        params: { labSlug: labAccount?.subDomain ?? '' },
      })
    }
    return next()
  }

  // - When logged in as a lab admin user, load the account from the url slug
  if (to.name === 'DesignSystem') {
    return next()
  }

  const labAccount = await accountStore.getLabAccountFromSlug(
    to.params.labSlug as string
  )

  if (!labAccount) {
    return next({
      name: 'App',
      params: { labSlug: appStore.defaultLabAdminSlug },
    })
  }

  return next()
})

// Lab admin guard
router.beforeEach((to, from, next) => {
  const userStore = useUserStore()

  if (!userStore.isLabAdminUser) {
    return next()
  }

  if (to.meta?.noLabAdminAccess) {
    return next({ name: 'App', params: { labSlug: to.params.labSlug } })
  }

  return next()
})

// Permissions guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  if (!authStore.isLoggedIn) {
    return next()
  }

  if (!to.meta?.permission) {
    return next()
  }

  // Routed through userStore.canUser() (not currentUser.canUser() directly)
  // so an active impersonation override is respected for route guards too.
  if (!userStore.currentUser || !userStore.canUser(to.meta?.permission)) {
    return next({ name: 'App', params: { labSlug: to.params.labSlug } })
  }

  return next()
})

// Unsaved changes guard
// Deliberately placed here: after the Auth and Permissions guards, so a
// navigation that would be rejected anyway never asks the user to throw work
// away first — and before the Title, Last seen and Data load guards, so a
// navigation the user cancels leaves no trace (no document.title change, no
// lastSeen write, no store fetches for a page never reached).
router.beforeEach(async (to, from, next) => {
  // A confirmed leave can be followed by a redirect from one of the guards
  // below, which starts a fresh navigation while the dirty surface is still
  // mounted. Without this, the user would be asked a second time for the
  // same departure.
  if (consumeLeaveConfirmation()) {
    return next()
  }

  if (!getBlockingClaims(to).length) {
    return next()
  }

  const shouldLeave = await confirmLeaveUnsaved()
  if (!shouldLeave) {
    return next(false)
  }

  markLeaveConfirmed()
  next()
})

// Clears a leave confirmation that was never consumed by a redirect, so it
// cannot leak into the user's next navigation.
router.afterEach(() => {
  consumeLeaveConfirmation()
})

// Title guard
router.beforeEach((to, from, next) => {
  const name = to.name
  const routeTitle = typeof name === 'string' ? name : String(name ?? '')
  document.title = `${getEnvTitlePrefix()}Milva Lab - ${routeTitle}`
  next()
})

// Last seen guard
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const userStore = useUserStore()

  if (authStore.isLoggedIn && !userStore.isLabAdminUser) {
    userStore.updateLastSeen()
  }

  next()
})

// Data load guard
router.beforeEach(async (to, from, next) => {
  // Standalone public route, no backend involvement at all (see Account and slug guard above) —
  // none of this app data is needed to show a plain approval/error confirmation.
  if (to.name === 'BcAdminConsentComplete') {
    return next()
  }

  // The public Display route hydrates its own stores directly from its own
  // JSON endpoint (see publicDisplayStore.ts) — it must never also run the
  // authenticated checklist below, even when the browser happens to carry a
  // logged-in session, or the two data sources would race and fight over
  // the same Pinia store state.
  if (to.name === 'PublicDisplay') {
    return next()
  }

  const checklist: string[] = []

  const authStore = useAuthStore()
  const accountStore = useAccountStore()
  const userStore = useUserStore()
  const inviteStore = useInviteStore()
  const labEquipmentStore = useLabEquipmentStore()
  const labLocationStore = useLabLocationStore()
  const bilagLibraryItemStore = useBilagLibraryItemStore()
  const analysisTestStore = useAnalysisTestStore()
  const analysisStore = useAnalysisStore()
  const turnaroundTimeStore = useTurnaroundTimeStore()
  const textListStore = useTextListStore()
  const pricingSegmentStore = usePricingSegmentStore()
  const addonServiceStore = useAddonServiceStore()
  const customerAccountStore = useCustomerAccountStore()
  const insuranceCustomerAccountStore = useInsuranceCustomerAccountStore()
  const orderStore = useOrderStore()
  const invoiceStore = useInvoiceStore()
  const closedDayStore = useClosedDayStore()
  const dropPointStore = useDropPointStore()
  const dhlShipmentStore = useDhlShipmentStore()
  const reportTemplateStore = useReportTemplateStore()
  const displayStore = useDisplayStore()
  const reportDescriptionStore = useReportDescriptionStore()
  const reportDescriptionItemStore = useReportDescriptionItemStore()
  const customerUserStore = useCustomerUserStore()
  const sampleAreaStore = useSampleAreaStore()
  const bcSettingsStore = useBcSettingsStore()
  const businessCentralStore = useBusinessCentralStore()

  // Add to checklist
  if (authStore.isLoggedIn) {
    checklist.push('allLabAccountUsersLoaded')
    checklist.push('allAnalysisTestsLoaded')
    checklist.push('allAnalysesLoaded')
    checklist.push('allSampleAreasLoaded')
    checklist.push('allTATsLoaded')
    checklist.push('allClosedDaysLoaded')
    checklist.push('allTextListsLoaded')
    // Also needed by the Translations > Pricing Segments tab, reachable
    // under translations.view alone (e.g. Lab Assistant) — without this,
    // that tab renders empty for anyone without setup.view too.
    if (
      userStore.canUser('setup.view') ||
      userStore.canUser('translations.view')
    ) {
      checklist.push('allPricingSegmentsLoaded')
    }
    // Also needed by the Translations > Tillægsydelser tab, reachable
    // under translations.view alone (e.g. Lab Assistant) — without this,
    // that tab renders empty for anyone without setup.view too.
    if (
      userStore.canUser('setup.view') ||
      userStore.canUser('translations.view')
    ) {
      checklist.push('allAddonServicesLoaded')
    }
    checklist.push('allDropPointsLoaded')
    checklist.push('allLabLocationsLoaded')
    checklist.push('allDhlShipmentsLoaded')
    checklist.push('allReportTemplatesLoaded')
    checklist.push('allDisplaysLoaded')
    checklist.push('allReportDescriptionsLoaded')
    checklist.push('allReportDescriptionItemsLoaded')
    checklist.push('allBilagLibraryItemsLoaded')

    if (userStore.canUser('employees.view')) {
      checklist.push('allActiveInvitesLoaded')
    }
    if (userStore.canUser('customers.view')) {
      checklist.push('allCustomerAccountsLoaded')
      checklist.push('allCustomerAccountPricesLoaded')
      checklist.push('allInsuranceCustomerAccountsLoaded')
      checklist.push('allInsuranceCustomerAccountPricesLoaded')
    }
    checklist.push('allOrdersLoaded')
    // Loaded for everyone, not just orders.billing: the All Orders status chip
    // turns red on a failed invoice sync (MIL-231), and that signal is useless
    // if it only reaches the users who could already see it on the Fakturaer
    // page. The Fakturaer page and the order's invoice tab stay gated — only
    // the data load is universal.
    checklist.push('allInvoicesLoaded')
    if (userStore.canUser('customers.view')) {
      checklist.push('customerUsersLoaded')
      checklist.push('customerUserInvitesLoaded')
      checklist.push('allCustomerPrivatePricesLoaded')
    }
    // Also needed by the Translations > Lab Equipment tab, reachable under
    // translations.view alone (e.g. Lab Assistant) — same reasoning as
    // Pricing Segments above.
    if (
      userStore.canUser('equipment.view') ||
      userStore.canUser('translations.view')
    ) {
      checklist.push('labEquipmentLoaded')
      checklist.push('labEquipmentMaintenanceLoaded')
    }

    if (userStore.isLabAdminUser) {
      checklist.push('allLabAccountsLoaded')
    }

    // Cheap flags stored directly on the account doc (already loaded by this point). Syncing
    // "connected" here — for every lab, not just BC-enabled ones — means BcSettingsView never
    // has to show a loading spinner while it waits to find out; only labs actually using BC pay
    // for the full settings/item-mappings fetch below.
    businessCentralStore.setConnected(
      accountStore.labAccount?.integration?.invoicing?.options.isConnected ?? false
    )
    const bcInvoicing = accountStore.labAccount?.integration?.invoicing
    if (bcInvoicing?.options.isEnabled) {
      checklist.push('bcSettingsLoaded')
    } else if (bcInvoicing?.type === 'business-central' && bcInvoicing.businessCentralIntegrationID) {
      // BC is off, but the lab may still hold invoices made on another BC connection (a mirrored Prod
      // invoice): the Fakturering card needs the lab's current connection to tell, so watch just that.
      bcSettingsStore.subscribeLiveInvoicingSettings(bcInvoicing.businessCentralIntegrationID)
    }
  }

  // Do the checklist
  const allPromises: Promise<unknown>[] = []
  if (checklist.includes('allAnalysisTestsLoaded')) {
    allPromises.push(analysisTestStore.getAnalysisTests())
  }
  if (checklist.includes('allAnalysesLoaded')) {
    allPromises.push(analysisStore.getAnalyses())
  }
  if (checklist.includes('allSampleAreasLoaded')) {
    allPromises.push(sampleAreaStore.getSampleAreas())
  }
  if (checklist.includes('allTATsLoaded')) {
    allPromises.push(turnaroundTimeStore.getTATs())
  }
  if (checklist.includes('allClosedDaysLoaded')) {
    allPromises.push(closedDayStore.getClosedDays())
  }
  if (checklist.includes('allTextListsLoaded')) {
    allPromises.push(textListStore.getTextLists())
  }
  if (checklist.includes('allPricingSegmentsLoaded')) {
    allPromises.push(pricingSegmentStore.getPricingSegments())
  }
  if (checklist.includes('allAddonServicesLoaded')) {
    allPromises.push(addonServiceStore.getAddonServices())
  }
  if (checklist.includes('allDropPointsLoaded')) {
    allPromises.push(dropPointStore.getDropPoints())
  }
  if (checklist.includes('allLabLocationsLoaded')) {
    allPromises.push(labLocationStore.getLabLocations())
  }
  if (checklist.includes('allDhlShipmentsLoaded')) {
    allPromises.push(dhlShipmentStore.getDhlShipments())
  }
  if (checklist.includes('allReportTemplatesLoaded')) {
    allPromises.push(reportTemplateStore.getReportTemplates())
  }
  if (checklist.includes('allDisplaysLoaded')) {
    allPromises.push(displayStore.getDisplays())
  }
  if (checklist.includes('allReportDescriptionsLoaded')) {
    allPromises.push(reportDescriptionStore.getReportDescriptions())
  }
  if (checklist.includes('allReportDescriptionItemsLoaded')) {
    allPromises.push(reportDescriptionItemStore.getReportDescriptionItems())
  }
  if (checklist.includes('allLabAccountUsersLoaded')) {
    allPromises.push(accountStore.getAllLabAccountUsers())
  }
  if (checklist.includes('allActiveInvitesLoaded')) {
    allPromises.push(inviteStore.getAllActiveInvites())
  }
  if (checklist.includes('allLabAccountsLoaded')) {
    allPromises.push(accountStore.getAllLabAccounts())
  }
  if (checklist.includes('allCustomerAccountsLoaded')) {
    allPromises.push(customerAccountStore.getCustomerAccounts())
  }
  if (checklist.includes('allCustomerAccountPricesLoaded')) {
    allPromises.push(customerAccountStore.getCustomerAccountPrices())
  }
  if (checklist.includes('allInsuranceCustomerAccountsLoaded')) {
    allPromises.push(insuranceCustomerAccountStore.getInsuranceCustomerAccounts())
  }
  if (checklist.includes('allInsuranceCustomerAccountPricesLoaded')) {
    allPromises.push(insuranceCustomerAccountStore.getInsuranceCustomerAccountPrices())
  }
  if (checklist.includes('allOrdersLoaded')) {
    allPromises.push(orderStore.getOrders())
  }
  if (checklist.includes('allInvoicesLoaded')) {
    allPromises.push(invoiceStore.getInvoices())
  }
  if (checklist.includes('customerUsersLoaded')) {
    allPromises.push(customerUserStore.getCustomerUsers())
  }
  if (checklist.includes('customerUserInvitesLoaded')) {
    allPromises.push(customerUserStore.getCustomerUserInvites())
  }
  if (checklist.includes('allCustomerPrivatePricesLoaded')) {
    allPromises.push(customerUserStore.getCustomerPrivatePrices())
  }
  if (checklist.includes('labEquipmentLoaded')) {
    allPromises.push(labEquipmentStore.getlabEquipment())
  }
  if (checklist.includes('labEquipmentMaintenanceLoaded')) {
    allPromises.push(labEquipmentStore.getLabEquipmentMaintenance())
  }
  if (checklist.includes('bcSettingsLoaded')) {
    allPromises.push(bcSettingsStore.loadSettings())
  }
  if (checklist.includes('allBilagLibraryItemsLoaded')) {
    allPromises.push(bilagLibraryItemStore.getBilagLibraryItems())
  }

  // Wait for checklist to complete
  await Promise.all(allPromises)

  next()
})

export default router

